import React from "react";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { Button } from "@/components/ui/button";
import SEO from "@/components/SEO";
import { SubpageNav, SubpageBackLink, SubpageFooter } from "@/components/SubpageLayout";
import {
  Megaphone, Users, Target, Mail, ArrowRight, Sparkles, Heart,
  Stethoscope, Beaker, Pill, ShoppingBag, Award, Shield, CheckCircle2,
} from "lucide-react";

const TX = {
  bg: {
    seo_title: "Реклама и партньорства",
    seo_desc: "Достигнете до здравно-съзнателна аудитория в Nutramilo — клиники, добавки, лаборатории, wellness брандове.",
    back: "Начало",
    badge: "Реклама и партньорства",
    hero_title: "Достигнете до здравно-съзнателна, висококачествена аудитория",
    hero_desc: "Nutramilo привлича хора, които активно инвестират в превенция, метаболитно здраве и персонализирано хранене. Партнирайте с нас за немащабен, здравно-релевантен маркетинг.",
    cta: "Свържете се с нас",
    audience_title: "Кои са нашите потребители?",
    audience_items: [
      { stat: "65%", label: "Възрастова група 30–55 г.", icon: "users" },
      { stat: "72%", label: "Висше образование", icon: "award" },
      { stat: "58%", label: "Активно ползват лаборатории и превантивни прегледи", icon: "shield" },
      { stat: "81%", label: "Готови да платят за качествени здравни продукти", icon: "heart" },
    ],
    audience_note: "Данни от вътрешен анкетен анализ на soft launch базата (n=420, Q1 2026). Не се продават индивидуални данни — всички партньорства са на агрегатно ниво.",
    formats_title: "Партньорски формати",
    formats: [
      {
        title: "Native контент",
        desc: "Спонсорирани статии в нашия Science / Blog раздел, написани с медицинска точност и разкрита спонсорска нотация.",
        icon: "megaphone",
        color: "#0EA5E9",
        examples: ["Образователни статии за добавки", "Lab partnership case studies", "Клинични интервюта"],
      },
      {
        title: "Препоръчани продукти",
        desc: "Curated списъци в Supplements Engine с прозрачна нотация \"платено партньорство\" и обективен научен контекст.",
        icon: "pill",
        color: "#10B981",
        examples: ["Витамини и микронутриенти", "Метаболитни support продукти", "Функционални храни"],
      },
      {
        title: "Клинични партньорства",
        desc: "Интеграция с клиники и лаборатории — Nutramilo насочва пациенти за специализирани прегледи и тестове.",
        icon: "stethoscope",
        color: "#8B5CF6",
        examples: ["Лабораторни тестове (DNA, хормони)", "Клиники за превенция", "Стоматологични скрининги"],
      },
      {
        title: "Бранд кампании",
        desc: "Brand awareness кампании за wellness брандове, които споделят нашите ценности на доказателствено-базирано здраве.",
        icon: "shopping-bag",
        color: "#F59E0B",
        examples: ["Continuous glucose monitors", "Wearables (Oura, Whoop)", "Premium храна и напитки"],
      },
    ],
    standards_title: "Нашите стандарти",
    standards: [
      "Само научно обосновани продукти и услуги",
      "Прозрачна нотация на всички спонсорства (\"Sponsored\" badge)",
      "Никакви misleading медицински твърдения",
      "Никакви продукти, противоречащи на превантивна медицина",
      "Право на отказ — не одобряваме партньорства, които не съответстват на ценностите ни",
      "GDPR-compliant — никога не споделяме лични данни на потребителите",
    ],
    pricing_title: "Какво включваме",
    pricing_bullets: [
      "Достъп до здравно-съзнателна аудитория в България и ЕС",
      "Multilingual поддръжка (BG / EN / ES)",
      "Прецизен audience targeting (метаболитни фенотипи P1–P5)",
      "Performance dashboards с conversion tracking",
      "Brand safety guarantee — съдържанието е модерирано",
      "Гъвкави CPM, CPC и flat-fee модели",
    ],
    contact_title: "Заявете медиа кит",
    contact_desc: "Изпратете ни кратко съобщение за вашия бранд и интерес — ще се свържем с вас в рамките на 48 часа.",
    contact_email: "partnerships@nutramilo.com",
  },
  en: {
    seo_title: "Advertising & Partnerships",
    seo_desc: "Reach a health-conscious audience on Nutramilo — clinics, supplements, labs, wellness brands.",
    back: "Home",
    badge: "Advertising & Partnerships",
    hero_title: "Reach a health-conscious, high-quality audience",
    hero_desc: "Nutramilo attracts people actively investing in prevention, metabolic health, and personalized nutrition. Partner with us for un-spammy, health-relevant marketing.",
    cta: "Get in touch",
    audience_title: "Who are our users?",
    audience_items: [
      { stat: "65%", label: "Age group 30–55", icon: "users" },
      { stat: "72%", label: "Higher education", icon: "award" },
      { stat: "58%", label: "Actively use labs and preventive checkups", icon: "shield" },
      { stat: "81%", label: "Willing to pay for quality health products", icon: "heart" },
    ],
    audience_note: "Data from internal soft launch survey analysis (n=420, Q1 2026). We never sell individual data — all partnerships are aggregate-level.",
    formats_title: "Partnership formats",
    formats: [
      {
        title: "Native content",
        desc: "Sponsored articles in our Science / Blog section, written with medical accuracy and disclosed sponsorship.",
        icon: "megaphone",
        color: "#0EA5E9",
        examples: ["Educational supplement articles", "Lab partnership case studies", "Clinical interviews"],
      },
      {
        title: "Featured products",
        desc: "Curated lists in our Supplements Engine with transparent \"paid partnership\" notation and objective scientific context.",
        icon: "pill",
        color: "#10B981",
        examples: ["Vitamins and micronutrients", "Metabolic support products", "Functional foods"],
      },
      {
        title: "Clinical partnerships",
        desc: "Integration with clinics and labs — Nutramilo refers patients for specialized checkups and tests.",
        icon: "stethoscope",
        color: "#8B5CF6",
        examples: ["Lab tests (DNA, hormones)", "Prevention clinics", "Dental screenings"],
      },
      {
        title: "Brand campaigns",
        desc: "Brand awareness campaigns for wellness brands sharing our evidence-based health values.",
        icon: "shopping-bag",
        color: "#F59E0B",
        examples: ["Continuous glucose monitors", "Wearables (Oura, Whoop)", "Premium foods and drinks"],
      },
    ],
    standards_title: "Our standards",
    standards: [
      "Only scientifically-grounded products and services",
      "Transparent disclosure of all sponsorships (\"Sponsored\" badge)",
      "No misleading medical claims",
      "No products contradicting preventive medicine",
      "Right of refusal — we don't accept partnerships misaligned with our values",
      "GDPR-compliant — we never share personal user data",
    ],
    pricing_title: "What we include",
    pricing_bullets: [
      "Access to a health-conscious audience in Bulgaria and EU",
      "Multilingual support (BG / EN / ES)",
      "Precise audience targeting (metabolic phenotypes P1–P5)",
      "Performance dashboards with conversion tracking",
      "Brand safety guarantee — content is moderated",
      "Flexible CPM, CPC, and flat-fee models",
    ],
    contact_title: "Request media kit",
    contact_desc: "Send us a brief message about your brand and interest — we'll get back to you within 48 hours.",
    contact_email: "partnerships@nutramilo.com",
  },
  es: {
    seo_title: "Publicidad y Asociaciones",
    seo_desc: "Llega a una audiencia preocupada por la salud en Nutramilo — clínicas, suplementos, laboratorios, marcas de bienestar.",
    back: "Inicio",
    badge: "Publicidad y Asociaciones",
    hero_title: "Llega a una audiencia de calidad, comprometida con su salud",
    hero_desc: "Nutramilo atrae a personas que invierten activamente en prevención, salud metabólica y nutrición personalizada. Asóciate con nosotros para un marketing relevante para la salud y sin spam.",
    cta: "Contáctanos",
    audience_title: "¿Quiénes son nuestros usuarios?",
    audience_items: [
      { stat: "65%", label: "Grupo de edad 30–55", icon: "users" },
      { stat: "72%", label: "Educación superior", icon: "award" },
      { stat: "58%", label: "Utilizan activamente laboratorios y chequeos preventivos", icon: "shield" },
      { stat: "81%", label: "Dispuestos a pagar por productos de salud de calidad", icon: "heart" },
    ],
    audience_note: "Datos del análisis de encuesta interna de lanzamiento suave (n=420, Q1 2026). Nunca vendemos datos individuales — todas las asociaciones son a nivel agregado.",
    formats_title: "Formatos de asociación",
    formats: [
      {
        title: "Contenido nativo",
        desc: "Artículos patrocinados en nuestra sección de Ciencia / Blog, redactados con precisión médica y con divulgación del patrocinio.",
        icon: "megaphone",
        color: "#0EA5E9",
        examples: ["Artículos educativos sobre suplementos", "Casos de estudio de asociaciones con laboratorios", "Entrevistas clínicas"],
      },
      {
        title: "Productos destacados",
        desc: "Listas seleccionadas en nuestro Motor de Suplementos con notación transparente de \"asociación de pago\" y contexto científico objetivo.",
        icon: "pill",
        color: "#10B981",
        examples: ["Vitaminas y micronutrientes", "Productos de apoyo metabólico", "Alimentos funcionales"],
      },
      {
        title: "Asociaciones clínicas",
        desc: "Integración con clínicas y laboratorios — Nutramilo deriva pacientes para chequeos y pruebas especializadas.",
        icon: "stethoscope",
        color: "#8B5CF6",
        examples: ["Pruebas de laboratorio (ADN, hormonas)", "Clínicas de prevención", "Revisiones dentales"],
      },
      {
        title: "Campañas de marca",
        desc: "Campañas de notoriedad de marca para marcas de bienestar que comparten nuestros valores de salud basados en evidencia.",
        icon: "shopping-bag",
        color: "#F59E0B",
        examples: ["Monitores continuos de glucosa", "Wearables (Oura, Whoop)", "Alimentos y bebidas premium"],
      },
    ],
    standards_title: "Nuestros estándares",
    standards: [
      "Solo productos y servicios con base científica",
      "Divulgación transparente de todos los patrocinios (insignia \"Patrocinado\")",
      "Sin afirmaciones médicas engañosas",
      "Sin productos que contradigan la medicina preventiva",
      "Derecho de rechazo — no aceptamos asociaciones que no se alineen con nuestros valores",
      "Cumplimiento GDPR — nunca compartimos datos personales de los usuarios",
    ],
    pricing_title: "Qué incluimos",
    pricing_bullets: [
      "Acceso a una audiencia preocupada por la salud en Bulgaria y la UE",
      "Soporte multilingüe (BG / EN / ES)",
      "Segmentación precisa de audiencia (fenotipos metabólicos P1–P5)",
      "Paneles de rendimiento con seguimiento de conversiones",
      "Garantía de seguridad de marca — el contenido está moderado",
      "Modelos flexibles de CPM, CPC y tarifa plana",
    ],
    contact_title: "Solicitar kit de medios",
    contact_desc: "Envíanos un breve mensaje sobre tu marca e interés — te responderemos en un plazo de 48 horas.",
    contact_email: "partnerships@nutramilo.com",
  },
};

const ICONS = {
  users: Users, award: Award, shield: Shield, heart: Heart,
  megaphone: Megaphone, pill: Pill, stethoscope: Stethoscope, "shopping-bag": ShoppingBag,
};

export default function AdvertisePage() {
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const t = TX[language] || TX.en;
  const bg = language === "bg";

  const sectionBg = isDark ? "bg-[hsl(223,47%,6%)]" : "bg-[#F9F9F7]";
  const altBg = isDark ? "bg-[hsl(223,47%,8%)]" : "bg-white";
  const heading = `text-2xl sm:text-3xl font-bold tracking-tight ${isDark ? "text-white" : "text-[#1A1A2E]"}`;
  const subheading = `text-lg font-bold ${isDark ? "text-white" : "text-[#1A1A2E]"}`;
  const body = `text-sm leading-relaxed ${isDark ? "text-slate-300" : "text-slate-600"}`;
  const badgeCls = `inline-flex items-center gap-2 px-3 py-1 rounded-full border text-xs font-medium ${isDark ? "border-[hsl(223,47%,20%)] text-slate-400" : "border-slate-200 text-slate-500"}`;

  return (
    <div className={`min-h-screen ${sectionBg}`} data-testid="advertise-page" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
      <SEO title={t.seo_title} description={t.seo_desc} path="/advertise" />
      <SubpageNav />

      {/* Hero */}
      <section className={`relative overflow-hidden ${altBg}`}>
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)", backgroundSize: "40px 40px" }} />
        <div className="max-w-4xl mx-auto px-4 py-16 sm:py-24 relative">
          <SubpageBackLink label={t.back} />
          <div className={badgeCls}><Megaphone className="w-3.5 h-3.5" /> {t.badge}</div>
          <h1 className={`text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mt-4 mb-5 leading-tight ${isDark ? "text-white" : "text-[#1A1A2E]"}`} style={{ fontFamily: "'Work Sans', sans-serif" }}>
            {t.hero_title}
          </h1>
          <p className={`text-base sm:text-lg leading-relaxed mb-8 max-w-2xl ${isDark ? "text-slate-300" : "text-slate-600"}`}>
            {t.hero_desc}
          </p>
          <Button asChild size="lg" data-testid="advertise-cta-btn"
            className="bg-gradient-to-r from-[#F59E0B] to-[#EF4444] hover:opacity-90 text-white text-sm font-semibold rounded-full px-8 h-12 shadow-lg shadow-amber-500/20">
            <a href={`mailto:${t.contact_email}?subject=Nutramilo%20media%20kit%20request`}>
              {t.cta} <ArrowRight className="w-4 h-4 ml-2" />
            </a>
          </Button>
        </div>
      </section>

      {/* Audience */}
      <section className={`py-14 ${sectionBg}`}>
        <div className="max-w-5xl mx-auto px-4">
          <div className="flex items-center gap-3 mb-8">
            <Users className="w-6 h-6 text-[#0EA5E9]" />
            <h2 className={heading}>{t.audience_title}</h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4" data-testid="advertise-audience-grid">
            {t.audience_items.map((it) => {
              const Icon = ICONS[it.icon] || Users;
              return (
                <div key={it.label} className={`p-5 rounded-2xl border ${isDark ? "bg-[hsl(223,47%,10%)] border-[hsl(223,47%,16%)]" : "bg-white border-slate-100 shadow-sm"}`}>
                  <Icon className="w-5 h-5 mb-2 text-[#0EA5E9]" />
                  <p className="text-3xl font-bold tracking-tight mb-1 text-[#0EA5E9]">{it.stat}</p>
                  <p className={`text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>{it.label}</p>
                </div>
              );
            })}
          </div>
          <p className={`text-[11px] italic max-w-3xl ${isDark ? "text-slate-500" : "text-slate-400"}`}>
            {t.audience_note}
          </p>
        </div>
      </section>

      {/* Formats */}
      <section className={`py-14 ${altBg}`}>
        <div className="max-w-5xl mx-auto px-4">
          <div className="flex items-center gap-3 mb-8">
            <Target className="w-6 h-6 text-[#8B5CF6]" />
            <h2 className={heading}>{t.formats_title}</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-testid="advertise-formats-grid">
            {t.formats.map((fmt) => {
              const Icon = ICONS[fmt.icon] || Megaphone;
              return (
                <div key={fmt.title} className={`p-6 rounded-2xl border ${isDark ? "bg-[hsl(223,47%,10%)] border-[hsl(223,47%,16%)]" : "bg-white border-slate-100 shadow-sm"}`}>
                  <Icon className="w-6 h-6 mb-3" style={{ color: fmt.color }} />
                  <h3 className={`${subheading} mb-2`}>{fmt.title}</h3>
                  <p className={`${body} mb-3`}>{fmt.desc}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {fmt.examples.map((ex) => (
                      <span key={ex} className={`text-[10px] px-2 py-1 rounded-full font-medium ${isDark ? "bg-white/5 text-slate-300" : "bg-slate-100 text-slate-600"}`}>{ex}</span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Standards */}
      <section className={`py-14 ${sectionBg}`}>
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex items-center gap-3 mb-8">
            <Shield className="w-6 h-6 text-[#10B981]" />
            <h2 className={heading}>{t.standards_title}</h2>
          </div>
          <ul className="space-y-3" data-testid="advertise-standards-list">
            {t.standards.map((s) => (
              <li key={s} className={`flex items-start gap-3 p-4 rounded-xl border ${isDark ? "bg-[hsl(223,47%,10%)] border-[hsl(223,47%,16%)]" : "bg-white border-slate-100"}`}>
                <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0 text-[#10B981]" />
                <span className={`text-sm ${isDark ? "text-slate-200" : "text-slate-700"}`}>{s}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* What we include */}
      <section className={`py-14 ${altBg}`}>
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex items-center gap-3 mb-8">
            <Sparkles className="w-6 h-6 text-[#F59E0B]" />
            <h2 className={heading}>{t.pricing_title}</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="advertise-pricing-grid">
            {t.pricing_bullets.map((b) => (
              <div key={b} className={`flex items-start gap-2 p-3 rounded-xl ${isDark ? "bg-[hsl(223,47%,10%)] border border-[hsl(223,47%,16%)]" : "bg-white border border-slate-100"}`}>
                <Beaker className="w-4 h-4 mt-0.5 flex-shrink-0 text-[#F59E0B]" />
                <span className={`text-sm ${isDark ? "text-slate-200" : "text-slate-700"}`}>{b}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className={`py-14 ${sectionBg}`}>
        <div className="max-w-3xl mx-auto px-4 text-center">
          <Mail className="w-8 h-8 mx-auto mb-4 text-[#F59E0B]" />
          <h2 className={`${heading} mb-3`}>{t.contact_title}</h2>
          <p className={`${body} mb-5 max-w-xl mx-auto`}>{t.contact_desc}</p>
          <a href={`mailto:${t.contact_email}?subject=Nutramilo%20partnership%20request`}
             data-testid="advertise-email-link"
             className="inline-block text-lg font-semibold text-[#F59E0B] hover:underline">
            {t.contact_email}
          </a>
        </div>
      </section>

      <SubpageFooter />
    </div>
  );
}
